export const MENTION_TOKEN_REGEX = /^@[^\s:]+:input:[^\s]+$/;

export type MentionInputPart = {
  type: 'text' | 'mention';
  value: string;
};

export const isMentionToken = (token: string): boolean => MENTION_TOKEN_REGEX.test(token);

export const parseMentionToken = (token: string): { modelKey: string; path: string } | null => {
  if (!isMentionToken(token)) {
    return null;
  }

  const tokenWithoutAt = token.startsWith('@') ? token.slice(1) : token;
  const inputIndex = tokenWithoutAt.indexOf(':input:');
  if (inputIndex === -1) {
    return null;
  }

  const modelKey = tokenWithoutAt.slice(0, inputIndex);
  const path = tokenWithoutAt.slice(inputIndex + 7);
  if (!modelKey || !path) {
    return null;
  }

  return { modelKey, path };
};

export const getMentionChipLabel = (token: string, labelByPath: Record<string, string>): string => {
  const parsed = parseMentionToken(token);
  if (!parsed) {
    return token;
  }

  const { path } = parsed;
  const resolvedLabel = labelByPath[path];
  if (resolvedLabel) {
    return resolvedLabel;
  }

  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] || path || token;
};

/**
 * Split composer/message text into plain-text segments and model-input mention tokens.
 */
export const parseInputIntoParts = (value: string): MentionInputPart[] => {
  const parts: MentionInputPart[] = [];
  const tokenRegex = /\S+/g;

  let cursor = 0;
  let match = tokenRegex.exec(value);
  while (match) {
    const token = match[0];
    const index = match.index;

    if (index > cursor) {
      parts.push({ type: 'text', value: value.slice(cursor, index) });
    }

    parts.push({
      type: isMentionToken(token) ? 'mention' : 'text',
      value: token,
    });

    cursor = index + token.length;
    match = tokenRegex.exec(value);
  }

  if (cursor < value.length) {
    parts.push({ type: 'text', value: value.slice(cursor) });
  }

  return parts;
};
